import { useState, useRef, useEffect } from "react";

// Import inhouse icons
import { ChevronDown, Info, Payments } from "@/components/icons_new";

// Import utils and helpers
import { formatCurrency } from "@/utils/formatCurrency";
import { ORDER_PAYMENT } from "@/utils/statusStyles";
import { useOrderDetailsContext } from "../context/OrderDetailsContext";

// Import reusable components
import RecordPaymentModal from "./RecordPaymentModal";

const resolveStatusStyle = (map, status) => {
  const fallback = map?.unknown ?? {
    label: "Unknown",
    color: "bg-[#F3F4F6] text-[#4B5563]",
  };

  if (!status || !map) return fallback;

  const normalized = status.toLowerCase().replace(/\s+/g, "");
  const entry = Object.entries(map).find(
    ([key]) => key.toLowerCase().replace(/\s+/g, "") === normalized
  );

  return entry ? map[entry[0]] : fallback;
};

export default function OrderPaymentSection() {
  const { order } = useOrderDetailsContext();
  const [showCollectPayments, setShowCollectPayments] = useState(false);

  if (!order) return null;

  const { pricing, paymentDetails, refund } = order;
  const paymentStatus = paymentDetails.status || order.paymentStatus;
  const paymentStyle = resolveStatusStyle(ORDER_PAYMENT, paymentStatus);

  const toAmount = (value) => (typeof value === "number" && Number.isFinite(value) ? value : 0);
  const lines = [
    { label: "Subtotal", value: pricing.subTotal },
    { label: "Discounts", value: pricing.discounts * -1, muted: pricing.discounts === 0 },
    { label: "Taxes", value: pricing.taxes, muted: pricing.taxes === 0 },
    { label: "Shipping", value: pricing.shipping, muted: pricing.shipping === 0 },
  ];

  return (
    <>
      <div className="rounded-md border border-[#d2d2d2] bg-white p-4">
        <div className="mb-4 flex items-center gap-2">
          <span
            className={`rounded-full h-fit flex items-center gap-[5px] px-2 py-1 text-[0.8em] font-medium ${paymentStyle.color}`}
          >
            <Payments className="h-[1.2em] w-[1.2em]"/>
            {paymentStyle.label}
          </span>
        </div>

        <div className="flex flex-col gap-2 text-sm text-[#333333]">
          {lines.map((line) => (
            <div key={line.label} className="flex items-center justify-between">
              <div className="flex gap-8 text-[#333333] font-medium">
                <span>{line.label}</span>
              </div>
              <span className={line.muted ? "text-[#333333] font-medium" : "text-[#333333] font-medium"}>
                {formatCurrency(toAmount(line.value))}
              </span>
            </div>
          ))}

          <div className="flex items-center justify-between border-t border-[#adadad] pt-3 text-base font-semibold text-[#333333]">
            <span>Total</span>
            <span>{formatCurrency(toAmount(pricing.total))}</span>
          </div>

          <div className="mt-2 flex items-center justify-between text-sm font-medium text-[#333333]">
            <span>Paid by customer</span>
            <span>{formatCurrency(toAmount(paymentDetails.amountPaid))}</span>
          </div>
          <div className="flex items-center justify-between text-sm font-medium text-[#333333]">
            <span>Balance due</span>
            <span>{formatCurrency(toAmount(paymentDetails.balanceDue))}</span>
          </div>

          {refund && refund.status && (
            <div className="mt-3 rounded-md border border-[#E0E7FF] bg-[#EEF2FF] px-4 py-3 text-sm text-[#312E81]">
              <div className="flex items-center gap-2 mb-2">
                <Info className="h-4 w-4" />
                <span className="text-[14px] font-semibold">Refund issued</span>
              </div>

              {/* Detail Section */}
              <div className="space-y-1 text-[13px] pl-6">
                <div className="flex justify-between">
                  <span className="text-[#333333] font-medium">Amount:</span>
                  <span className="font-semibold">{formatCurrency(toAmount(refund.amount))}</span>
                </div>

                {refund.notes && (
                  <div className="flex justify-between">
                    <span className="text-[#333333] font-medium">Refund Method:</span>
                    <span className="font-medium max-w-[50%]">{refund.notes}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            className="rounded-[10px] border border-[#c6c6c6] bg-[#333333] px-[8px] py-[5px] text-sm font-medium text-[#ffffff] hover:bg-[#333333d8]"
          >
            Send invoice
          </button>

          <button
            type="button"
            onClick={() => setShowCollectPayments(true)}
            className="flex items-center gap-2 rounded-[10px] border border-[#c6c6c6] bg-[#333333] px-[8px] py-[5px] text-sm font-medium text-[#ffffff] hover:bg-[#333333d8]"    
          >
            Collect payment
          </button>
        </div>
      </div>

      {showCollectPayments && (
        <RecordPaymentModal 
          onClose={() => setShowCollectPayments(false)}
          order={order}
        />
      )}
    </>
  );
}
